import { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { Spinner } from "@/shared/components/spinner";
import { EmptyState } from "@/shared/components/EmptyState";
import { Pagination } from "@/shared/components/Pagination";
import { cn } from "@/shared/lib/utils";
import {
  getDocumentPages,
  getDocumentFile,
  getDiagnosticReportFile,
  DocumentApiError,
  type DocumentPagesResponse,
  type DocumentFileResponse,
  type DocumentPage,
  type DocumentPageStatus,
} from "@/features/patient/records/api/documentPagesApi";
import { useUploadHistory } from "@/portals/patient/context/UploadHistoryContext";

// A stored "document" isn't always a scanned image or PDF — Epic's own
// presentedForm/content attachment is sometimes just the provider's
// narrative report as plain text/HTML/XML (see epic_integration/service.py's
// _ALLOWED_BINARY_CONTENT_TYPES comment: confirmed against a live Epic
// sandbox, a genuine discharge summary and a radiology report both came
// back this way, no actual image involved). Rendering that raw markup in
// an iframe, in the exact spot an actual scanned document/image would
// appear, reads as "this IS the document" and is genuinely confusing —
// isVisuallyRenderable draws the line FilePane's body below uses to decide
// between an actual preview and a clear "no image/PDF" empty state.
export function isVisuallyRenderable(contentType: string | null): boolean {
  if (!contentType) return false;
  return contentType === "application/pdf" || contentType.startsWith("image/");
}

const PAGE_SIZE = 5;
// "A few seconds" per the extraction-progress UX — long documents (hundreds of
// pages) can take minutes end-to-end, so this only needs to feel responsive,
// not real-time.
const POLL_INTERVAL_MS = 4000;
// Without a cap, a backend job that never reaches a terminal status (worker
// crash, stuck extraction) leaves this polling forever with no indication
// anything is wrong. 5 minutes comfortably covers a large multi-page document.
const MAX_POLL_DURATION_MS = 5 * 60 * 1000;
// Below this length, a page's text is shown in full with no expand control.
const COLLAPSE_THRESHOLD = 320;

const PAGE_STATUS_META: Record<DocumentPageStatus, { label: string; icon: string; badgeClass: string }> = {
  extracted: { label: "Extracted", icon: "lucide:check-circle-2", badgeClass: "bg-[#F0FDF4] text-[#16A34A]" },
  needs_review: { label: "Needs review", icon: "lucide:alert-triangle", badgeClass: "bg-[#FFFBEB] text-[#B45309]" },
  failed: { label: "Couldn't be read", icon: "lucide:alert-circle", badgeClass: "bg-[#FEF2F2] text-[#DC2626]" },
};

/** Translates a 0-100 confidence score into plain language — thresholds mirror the
 *  backend's own review cutoff (CONFIDENCE_REVIEW_THRESHOLD = 80). */
function confidenceLabel(confidence: number | null): { text: string; tone: "good" | "warn" | "bad" } | null {
  if (confidence === null) return null;
  if (confidence >= 80) return { text: "High confidence", tone: "good" };
  if (confidence >= 50) return { text: "Medium confidence — some words may be unclear", tone: "warn" };
  return { text: "Low confidence — may contain errors", tone: "bad" };
}

const CONFIDENCE_TONE_CLASS: Record<"good" | "warn" | "bad", string> = {
  good: "text-[#16A34A]",
  warn: "text-[#B45309]",
  bad: "text-[#DC2626]",
};

function PageTextBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsCollapse = text.length > COLLAPSE_THRESHOLD;
  const preview = needsCollapse ? `${text.slice(0, COLLAPSE_THRESHOLD).trimEnd()}…` : text;

  return (
    <div>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-[#374151]">
        {expanded || !needsCollapse ? text : preview}
      </p>
      {needsCollapse && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="!mt-2 !h-auto !w-full !justify-center !rounded-lg !p-2 !text-xs !font-semibold !text-[#4B5563] hover:!bg-[#FFF0F3] hover:!text-[#E4002B]"
        >
          <Icon icon={expanded ? "lucide:chevron-up" : "lucide:chevron-down"} className="mr-1.5 h-3.5 w-3.5" />
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
}

function PageRow({ page }: { page: DocumentPage }) {
  const meta = PAGE_STATUS_META[page.status];
  const confidence = confidenceLabel(page.confidence);

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold text-[#0A2947]">Page {page.pageNumber}</p>
        <span className={cn("flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold", meta.badgeClass)}>
          <Icon icon={meta.icon} className="h-3 w-3" />
          {meta.label}
        </span>
      </div>

      {confidence && (
        <p className={cn("mt-1.5 text-xs font-semibold", CONFIDENCE_TONE_CLASS[confidence.tone])}>
          {confidence.text}
        </p>
      )}

      <div className="mt-3">
        {page.status === "failed" ? (
          <p className="flex items-center gap-2 rounded-xl bg-[#FEF2F2] px-3 py-2.5 text-xs font-semibold text-[#DC2626]">
            <Icon icon="lucide:alert-circle" className="h-4 w-4 shrink-0" />
            This page couldn't be read. It may be blank, badly scanned, or in a format we can't process.
          </p>
        ) : page.extractedText ? (
          <>
            {page.status === "needs_review" && (
              <p className="mb-2 flex items-center gap-2 rounded-xl bg-[#FFFBEB] px-3 py-2.5 text-xs font-semibold text-[#B45309]">
                <Icon icon="lucide:alert-triangle" className="h-4 w-4 shrink-0" />
                This text may contain reading errors — double-check anything important.
              </p>
            )}
            <PageTextBlock text={page.extractedText} />
          </>
        ) : (
          <p className="text-xs font-medium text-[#9CA3AF]">No text was found on this page.</p>
        )}
      </div>
    </div>
  );
}

/** The actual file, rendered inline. An image is shown directly (via
 *  previewUrl — a web-safe rendition the browser can always display, even
 *  for a TIFF); a PDF is handed to the browser's own viewer via iframe,
 *  which already scrolls through every page — no extra rendering library
 *  needed. fileUrl is always the true original, offered as a download.
 *
 *  diagnosticReportId, when given, is preferred over documentReferenceId —
 *  it's the DiagnosticReport's own presentedForm attachment (downloaded by
 *  epic_integration/service.py::fetch_binary), a precise source rather than
 *  documentReferenceId's same-day-date guess. If BOTH are given and the
 *  report itself has no stored file (fetch_binary never ran for it, or
 *  rejected the attachment), this falls back to the matched
 *  DocumentReference — the pre-existing behavior — rather than showing
 *  "not available" when a viewable file might still exist there. */
export function FilePane({
  documentReferenceId,
  diagnosticReportId,
  actingAsProfileId,
}: {
  documentReferenceId?: string;
  diagnosticReportId?: string;
  actingAsProfileId?: string;
}) {
  const [file, setFile] = useState<DocumentFileResponse | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const { getCachedDocumentFile, cacheDocumentFile } = useUploadHistory();

  const primaryId = diagnosticReportId ?? documentReferenceId ?? "";
  // Prefixed so a DiagnosticReport id can never collide with a
  // DocumentReference id in the shared cache map.
  const cacheKey = diagnosticReportId ? `diagnostic-report:${diagnosticReportId}` : primaryId;

  // Always goes to the network and refreshes the cache — used by Retry and
  // by the <img> onError (a link that died early), where a cache re-read
  // would just hand back the same stale URL.
  const fetchFile = () => {
    setImageLoadFailed(false);
    const emptyResult: DocumentFileResponse = {
      success: true, documentReferenceId: primaryId, fileUrl: null, previewUrl: null, thumbnailUrl: null, contentType: null,
    };
    const primaryFetch = diagnosticReportId
      ? getDiagnosticReportFile(diagnosticReportId, actingAsProfileId)
      : getDocumentFile(documentReferenceId as string, actingAsProfileId);

    primaryFetch
      .then((result) => {
        if (result.fileUrl || !diagnosticReportId || !documentReferenceId) {
          setFile(result);
          cacheDocumentFile(cacheKey, result);
          return;
        }
        // The report has no stored file of its own — fall back to the
        // same-day-matched DocumentReference.
        return getDocumentFile(documentReferenceId, actingAsProfileId).then((fallback) => {
          setFile(fallback);
          cacheDocumentFile(cacheKey, fallback);
        });
      })
      // A transient failure is deliberately NOT cached — otherwise one blip
      // would wedge this at "not available" for the whole cache TTL.
      .catch(() => setFile(emptyResult));
  };

  useEffect(() => {
    // A fresh-enough cached response (same presigned URLs) redisplays
    // instantly with no spinner and no file re-download; otherwise fetch.
    const cached = getCachedDocumentFile(cacheKey);
    if (cached) {
      setImageLoadFailed(false);
      setFile(cached);
    } else {
      setFile(null);
      fetchFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const isImage = (file?.contentType ?? "").startsWith("image/");
  // previewUrl is a browser-safe rendition; for an image it may be absent
  // (a TIFF whose PNG preview generation was skipped) — fall back to the
  // thumbnail so there's still something to look at, and always keep the
  // "open original" link to the true original.
  const imageSrc = file?.previewUrl ?? file?.thumbnailUrl ?? null;

  // Same treatment as the "View Recent Uploads" button on the Upload Records
  // page (see UploadRecords.tsx): brand red at 50%, white extrabold text,
  // rounded-xl, darkening slightly on hover.
  const openOriginalLink = file?.fileUrl ? (
    <a
      href={file.fileUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex shrink-0 items-center rounded-lg bg-[#E4002B]/50 px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#E4002B]/60"
    >
      Open original
    </a>
  ) : null;

  // "Original Document" and "Open original" share one line above the viewer.
  const header = (
    <div className="mb-2 flex min-h-[40px] items-center justify-between gap-3">
      <h3 className="text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">Original Document</h3>
      {openOriginalLink}
    </div>
  );

  let body: React.ReactNode;
  if (!file) {
    body = (
      <div className="flex h-[70vh] items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white">
        <Spinner />
      </div>
    );
  } else if (!file.fileUrl) {
    body = (
      <EmptyState
        className="h-[70vh] justify-center"
        icon={<Icon icon="lucide:file-x" className="h-8 w-8 text-[#9CA3AF]" />}
        title="Original file not available"
        description="We don't have a viewable copy of this file stored."
      />
    );
  } else if (isImage && (imageLoadFailed || !imageSrc)) {
    body = (
      <div className="flex h-[70vh] flex-col items-center justify-center gap-3 rounded-2xl border border-[#E5E7EB] bg-[#FAFAFA] p-4 text-center">
        <Icon icon="lucide:image-off" className="h-8 w-8 text-[#9CA3AF]" />
        <p className="text-xs font-medium text-[#6B7280]">
          {imageLoadFailed ? "The preview link expired." : "We couldn't build a preview for this image."}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchFile} className="!h-8 !rounded-full !border-[#E5E7EB] !px-3 !text-xs !font-semibold">
            Retry
          </Button>
          {openOriginalLink}
        </div>
      </div>
    );
  } else if (isImage) {
    body = (
      <div className="max-h-[75vh] overflow-y-auto rounded-2xl border border-[#E5E7EB] bg-[#0A2947]">
        <img
          src={imageSrc ?? undefined}
          alt="Original document"
          className="w-full object-contain"
          onError={() => setImageLoadFailed(true)}
        />
      </div>
    );
  } else if (!isVisuallyRenderable(file.contentType)) {
    // A real, successfully-fetched file exists — it's just not a scanned
    // image or PDF (a provider's narrative report text, stored as plain
    // text/HTML/XML — see isVisuallyRenderable's own comment). A clear
    // empty state instead of dumping that raw markup where a preview would
    // normally appear; "Open original" in the header above still works, so
    // nothing is actually hidden, only mislabeled as "the document".
    body = (
      <EmptyState
        className="h-[70vh] justify-center"
        icon={<Icon icon="lucide:file-text" className="h-8 w-8 text-[#9CA3AF]" />}
        title="No image or PDF available"
        description={'This record only includes a text report, not a scanned document or image. Use "Open original" above to read the full text.'}
      />
    );
  } else {
    // PDF — scrollable by default via the browser's built-in viewer, one
    // continuous document.
    body = (
      <iframe
        src={file.fileUrl}
        title="Original document"
        className="h-[75vh] w-full rounded-2xl border border-[#E5E7EB] bg-white"
      />
    );
  }

  return (
    <div>
      {header}
      {body}
    </div>
  );
}

function ExtractedPagesPane({ documentReferenceId, actingAsProfileId }: { documentReferenceId: string; actingAsProfileId?: string }) {
  const [page, setPage] = useState(1);
  const [pagesResponse, setPagesResponse] = useState<DocumentPagesResponse | null>(null);
  const [loadError, setLoadError] = useState<DocumentApiError | null>(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const { getCachedDocumentPages, cacheDocumentPages } = useUploadHistory();

  useEffect(() => {
    setPage(1);
  }, [documentReferenceId]);

  useEffect(() => {
    const guard = { cancelled: false, paused: false, timeoutId: undefined as number | undefined };
    const pollStartedAt = Date.now();

    const runFetch = async () => {
      try {
        const result = await getDocumentPages(documentReferenceId, {
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
          actingAsProfileId,
        });
        if (guard.cancelled) return;
        setPagesResponse(result);
        cacheDocumentPages(documentReferenceId, page, result);
        setLoadError(null);
        if (result.status === "in_progress" || result.status === "not_started") {
          scheduleNext();
        } else {
          setPollTimedOut(false);
        }
      } catch (err) {
        if (guard.cancelled) return;
        setLoadError(err instanceof DocumentApiError ? err : new DocumentApiError(0, "Unable to load this document."));
      }
    };

    const scheduleNext = () => {
      if (guard.cancelled) return;
      if (Date.now() - pollStartedAt >= MAX_POLL_DURATION_MS) {
        setPollTimedOut(true);
        return;
      }
      if (document.visibilityState === "hidden") {
        guard.paused = true;
        return;
      }
      guard.timeoutId = window.setTimeout(runFetch, POLL_INTERVAL_MS);
    };

    const onVisibilityChange = () => {
      if (guard.cancelled || !guard.paused) return;
      if (document.visibilityState === "visible") {
        guard.paused = false;
        runFetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    setLoadError(null);
    setPollTimedOut(false);

    const cached = getCachedDocumentPages(documentReferenceId, page);
    if (cached) {
      setPagesResponse(cached);
      if (cached.status === "in_progress" || cached.status === "not_started") {
        runFetch();
      }
    } else {
      setPagesResponse(null);
      runFetch();
    }

    return () => {
      guard.cancelled = true;
      if (guard.timeoutId) window.clearTimeout(guard.timeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [documentReferenceId, page, actingAsProfileId, retryToken, getCachedDocumentPages, cacheDocumentPages]);

  const retryLoad = () => setRetryToken((t) => t + 1);

  const isNotFound = loadError?.status === 404;
  const status = pagesResponse?.status;
  const isProcessing = status === "in_progress" || status === "not_started";
  const isFailed = status === "failed";
  const totalPages = pagesResponse?.totalPages ?? null;
  const processedCount = pagesResponse ? pagesResponse.extractedPages + pagesResponse.failedPages : 0;
  const isEmpty = status === "complete" && (totalPages ?? 0) === 0;
  const paginationTotal = totalPages ?? pagesResponse?.count ?? 0;

  return (
    <div className="max-h-[75vh] overflow-y-auto rounded-2xl border border-[#E5E7EB] bg-white p-4">
      {loadError && !isNotFound && (
        <EmptyState
          icon={<Icon icon="lucide:wifi-off" className="h-8 w-8 text-[#9CA3AF]" />}
          title="We couldn't load this document"
          description="Check your connection and try again."
          action={
            <Button variant="outline" onClick={retryLoad} className="!h-9 !rounded-full !border-[#E5E7EB] !px-4 !text-xs !font-bold">
              Try again
            </Button>
          }
        />
      )}

      {loadError && isNotFound && (
        <EmptyState
          icon={<Icon icon="lucide:file-question" className="h-8 w-8 text-[#9CA3AF]" />}
          title="This document isn't available"
          description="It may have been removed, or you may not have access to it."
        />
      )}

      {!loadError && !pagesResponse && (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-[#8A8A8A]">
          <Spinner />
          Loading…
        </div>
      )}

      {!loadError && pagesResponse && (
        <>
          {isProcessing && pollTimedOut && (
            <EmptyState
              icon={<Icon icon="lucide:clock-alert" className="h-8 w-8 text-[#9CA3AF]" />}
              title="This is taking longer than expected"
              description={
                totalPages != null
                  ? `Still processing — ${processedCount} of ${totalPages} pages complete. Check back in a few minutes.`
                  : "We're still working on this document. Check back in a few minutes."
              }
              action={
                <Button variant="outline" onClick={retryLoad} className="!h-9 !rounded-full !border-[#E5E7EB] !px-4 !text-xs !font-bold">
                  Check again
                </Button>
              }
            />
          )}

          {isProcessing && !pollTimedOut && (
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#F7B4C3] bg-[#FFF7F8] px-4 py-3">
              <Icon icon="lucide:loader-2" className="h-5 w-5 shrink-0 animate-spin text-[#F17893]" />
              <p className="text-sm font-semibold text-[#0A2947]">
                {totalPages != null ? `Processing — ${processedCount} of ${totalPages} pages complete` : "Processing your document…"}
              </p>
            </div>
          )}

          {isFailed && (
            <EmptyState
              icon={<Icon icon="lucide:alert-circle" className="h-8 w-8 text-[#DC2626]" />}
              title="This document could not be processed"
              description="We weren't able to read this file. You can try uploading it again."
            />
          )}

          {!isFailed && isEmpty && (
            <EmptyState
              icon={<Icon icon="lucide:file-x" className="h-8 w-8 text-[#9CA3AF]" />}
              title="No readable text was found"
              description="We couldn't find any text in this document."
            />
          )}

          {!isFailed && !isEmpty && pagesResponse.pages.length > 0 && (
            <div className="space-y-3">
              {pagesResponse.pages.map((p) => (
                <PageRow key={p.pageNumber} page={p} />
              ))}
            </div>
          )}

          {!isFailed && !isEmpty && paginationTotal > PAGE_SIZE && (
            <div className="mt-5">
              <Pagination page={page} limit={PAGE_SIZE} total={paginationTotal} onChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** "View Original File" tab body — the real document/image next to what we
 *  read out of it, for one document. Replaces the old floating "View
 *  Extracted Text" button + its small image-thumbnail-that-opens-a-new-tab:
 *  this shows the actual file inline (scrollable for a multi-page PDF, via
 *  the browser's own viewer) side by side with the same per-page text/
 *  confidence breakdown that used to live behind that button. */
export function OriginalFileTab({ documentReferenceId, actingAsProfileId }: { documentReferenceId: string; actingAsProfileId?: string }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {/* FilePane renders its own "Original Document" header row (with the
          "Open original" link on the same line). */}
      <FilePane documentReferenceId={documentReferenceId} actingAsProfileId={actingAsProfileId} />
      <div>
        <div className="mb-2 flex min-h-[40px] items-center">
          <h3 className="text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">Extracted Content</h3>
        </div>
        <ExtractedPagesPane documentReferenceId={documentReferenceId} actingAsProfileId={actingAsProfileId} />
      </div>
    </div>
  );
}
